import { BaseIcon, Loader, Modal } from "@shltelemedicine/shl-ui-lib";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import linkUsersIcon from "../../../../assets/management/link_users.svg";
import {
  ManageStudiesActionType,
  useManageStudiesContext,
} from "../../../../context/ManageStudiesContext";
import useAlert from "../../../../hooks/useAlert";
import useResponsive from "../../../../hooks/useResponsive";
import { IStudyUser } from "../../../../models/study.interface";
import { IUser } from "../../../../models/users.interface";
import studiesService from "../../../../services/studies.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import { TableRef } from "../../../Shared/Table/Table";
import LinkableUsersTable from "./LinkableUsersTable/LinkableUsersTable";
import LinkableUsersTableHeader from "./LinkableUsersTable/LinkableUsersTableHeader/LinkableUsersTableHeader";
import styles from "./LinkUsersToStudyModal.module.css";

type LinkUsersToStudyModalProps = {
  show?: boolean;
  study?: {
    id: string;
    name: string;
    currentUsers?: IStudyUser[];
    totalLinkedUsers: number;
  };
  onSave?: () => void;
  onCancel?: () => void;
};

const LinkUsersToStudyModal = ({
  show,
  study,
  onSave,
  onCancel,
}: LinkUsersToStudyModalProps) => {
  const {
    id: studyId,
    name: studyName,
    totalLinkedUsers = 0,
    currentUsers: studyUsers,
  } = study ?? {};

  const { loadStudies, dispatch } = useManageStudiesContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const [linkableUsers, setLinkableUsers] = useState<IUser[] | null>([]);
  const [tableRef, setTableRef] = useState<TableRef | null>(null);
  const [search, setSearch] = useState(tableRef?.globalFilter);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isMobile } = useResponsive();

  useEffect(() => {
    if (show) {
      (async () => {
        setLoading(true);
        setLinkableUsers([]);

        const availableUsers = await studiesService.getStudyAvailableUsers();

        // Remove users that already linked
        const filteredLinkableUsers =
          availableUsers?.filter(
            (u) =>
              !studyUsers?.find(
                (studyUser) => studyUser.id === u.id && studyUser.isLinked
              )
          ) ?? [];

        setLinkableUsers(filteredLinkableUsers);
        setLoading(false);
      })();
    }
  }, [show, studyUsers]);

  const refChangeHandler = useCallback((ref: TableRef) => {
    if (ref !== null) {
      setTableRef(ref);
    }
  }, []);

  const searchChangeHandler = (text: string) => {
    setSearch(text);
    tableRef?.setGlobalFilter(text || undefined);
  };

  const saveHandler = async () => {
    try {
      setSaving(true);
      const {
        success,
        errorCode,
      } = await studiesService.toggleUsersLinkToStudy(
        tableRef!.selectedCheckedRows!.map((user) => user.id),
        studyId!,
        true
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.TOGGLE_USER_LINK_TO_STUDY",
          "An error occurred while trying to link a user to a study.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      loadStudies({ toggleLoading: false });

      dispatch({
        type: ManageStudiesActionType.SET_LINK_USERS_MODAL,
        payload: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const totalUsers = (linkableUsers?.length ?? 0) + (totalLinkedUsers ?? 0);
  const totalSelected = tableRef?.selectedCheckedRows?.length ?? 0;

  return (
    <>
      <Modal
        dialogClassName={`${!isMobile && styles.dialog}`}
        bodyClassName="d-flex flex-column overflow-hidden"
        contentClassName="h-75"
        centered
        backdrop="static"
        show={show}
        onHide={onCancel}
        header={
          <div className="d-flex align-items-center">
            <BaseIcon icon={linkUsersIcon} className={styles.headerIcon} />

            <span className="mx-1"></span>
            <p className="mb-0">
              {studyName} &gt; &nbsp;
              <FormattedMessage
                id="MANAGEMENT.STUDIES.LINK_USERS"
                defaultMessage="Link Users"
              />
            </p>
          </div>
        }
        footer={
          <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-sm-0 ">
            <CancelButton onClick={onCancel} className="mx-2" />
            <SaveButton
              onClick={saveHandler}
              className="prim-bg-gradient-color"
              disabled={
                saving || loading || !tableRef?.selectedCheckedRows.length
              }
            >
              <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
              {saving && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </SaveButton>
          </div>
        }
      >
        <LinkableUsersTableHeader
          totalUsers={totalUsers}
          totalSelected={totalSelected}
          totalLinkedUsers={totalLinkedUsers}
          disabled={loading || saving}
          searchText={search}
          onSearchTextChange={searchChangeHandler}
        />
        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-hidden flex-grow-1">
            {!linkableUsers?.length ? (
              <h5 className="px-3 pt-5 text-center">
                <FormattedMessage
                  id="MANAGEMENT.STUDIES.ALL_USERS_LINKED_TO_STUDY"
                  defaultMessage="All available users already linked to this study"
                />
              </h5>
            ) : (
              <LinkableUsersTable
                users={linkableUsers ?? []}
                tableRef={refChangeHandler}
                disabled={loading || saving}
                highlightText={search}
              />
            )}
          </div>
        )}
      </Modal>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default LinkUsersToStudyModal;
